<template>
  <v-dialog
    :model-value="modelValue"
    class="us-team-dialog-root"
    content-class="us-team-dialog-root"
    max-width="560"
    persistent
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card rounded="xl" class="user-dialog-card us-team-dialog">
      <div class="dialog-header us-team-dialog__header">
        <div class="us-team-dialog__header-main">
          <div class="dialog-header__icon us-team-dialog__icon-wrap us-team-dialog__icon-wrap--edit" aria-hidden="true">
            <v-icon icon="mdi-account-edit-outline" size="22" />
          </div>
          <div class="us-team-dialog__head-copy">
            <span class="dialog-header__title">Edit team member</span>
            <span class="us-team-dialog__subtitle">
              {{ subtitle }}
            </span>
          </div>
        </div>
        <v-btn
          icon
          variant="flat"
          size="small"
          class="dialog-close-btn us-team-dialog__close"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body us-team-dialog__body">
        <v-form ref="formRef" v-model="isValid" @submit.prevent="submit">
          <div class="us-team-dialog__type-badge" :class="typeBadgeClass">
            <v-icon :icon="typeIcon" size="16" />
            <span>{{ typeLabel }}</span>
          </div>

          <section class="us-team-dialog__section">
            <h3 class="us-team-dialog__section-title">
              {{ isAiAgentSelected ? 'AI profile' : 'Profile' }}
            </h3>

            <label class="us-team-dialog__field">
              <span class="us-team-dialog__label">
                {{ isAiAgentSelected ? 'Agent name' : 'Full name' }}
              </span>
              <v-text-field
                v-model="form.user_name"
                :placeholder="isAiAgentSelected ? 'e.g. Sales AI' : 'Jane Smith'"
                prepend-inner-icon="mdi-account-outline"
                variant="outlined"
                density="comfortable"
                color="primary"
                hide-details="auto"
                :rules="nameRules"
                class="us-team-dialog__input"
              />
            </label>

            <div v-if="isAiAgentSelected" class="us-team-dialog__field-row">
              <label class="us-team-dialog__field us-team-dialog__field--half">
                <span class="us-team-dialog__label">Voice gender</span>
                <v-select
                  v-model="form.agent_gender"
                  :items="genderOptions"
                  item-title="label"
                  item-value="value"
                  placeholder="Select gender"
                  prepend-inner-icon="mdi-gender-male-female"
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  hide-details="auto"
                  class="us-team-dialog__input"
                />
              </label>

              <label class="us-team-dialog__field us-team-dialog__field--half">
                <span class="us-team-dialog__label">Voice language</span>
                <v-select
                  v-model="form.agent_language"
                  :items="languageOptions"
                  item-title="label"
                  item-value="value"
                  placeholder="Select language"
                  prepend-inner-icon="mdi-translate"
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  hide-details="auto"
                  class="us-team-dialog__input"
                />
              </label>
            </div>
          </section>

          <section v-if="!isAiAgentSelected" class="us-team-dialog__section">
            <h3 class="us-team-dialog__section-title">Contact</h3>

            <label class="us-team-dialog__field">
              <span class="us-team-dialog__label">Mobile number</span>
              <div class="us-team-dialog__phone-row">
                <v-autocomplete
                  v-model="form.country_code"
                  :items="countryOptions"
                  item-title="label"
                  item-value="dial_code"
                  placeholder="+91"
                  prepend-inner-icon="mdi-earth"
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  hide-details="auto"
                  :rules="countryCodeRules"
                  class="us-team-dialog__input us-team-dialog__phone-code"
                  menu-icon="mdi-chevron-down"
                />
                <v-text-field
                  v-model="form.mobile_number"
                  placeholder="10-digit number"
                  prepend-inner-icon="mdi-phone-outline"
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  hide-details="auto"
                  maxlength="10"
                  inputmode="numeric"
                  :rules="mobileNumberRules"
                  class="us-team-dialog__input"
                  @update:model-value="onMobileNumberInput"
                />
              </div>
            </label>
          </section>

          <section v-if="!isAiAgentSelected" class="us-team-dialog__section">
            <h3 class="us-team-dialog__section-title">Business hours</h3>
            <p class="us-team-dialog__hint">
              When this person is a CALL_REDIRECT target, they're only
              reachable within these hours. Leave blank to use the org's own
              hours ({{ orgBusinessHoursLabel }}).
            </p>
            <div class="us-team-dialog__phone-row">
              <v-text-field
                v-model="form.business_hours_start"
                type="time"
                label="Start"
                variant="outlined"
                density="comfortable"
                color="primary"
                hide-details="auto"
                class="us-team-dialog__input"
              />
              <v-text-field
                v-model="form.business_hours_end"
                type="time"
                label="End"
                variant="outlined"
                density="comfortable"
                color="primary"
                hide-details="auto"
                class="us-team-dialog__input"
              />
            </div>
          </section>
        </v-form>
      </v-card-text>

      <v-card-actions class="dialog-footer us-team-dialog__footer">
        <v-spacer />
        <v-btn
          variant="flat"
          class="text-none us-team-dialog__cancel"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          variant="flat"
          class="text-none btn-primary-cta us-team-dialog__save"
          :loading="saving"
          :disabled="!canSave"
          rounded="lg"
          @click="submit"
        >
          Save changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import orgUsersApi from '@/mixins/Users/orgUsersApi'
import { isApiSuccess } from '@/mixins/Extras/ApiResponse'
import countryCodeList from '@/JsonFiles/CountryCodeList.json'
import {
  DEFAULT_COUNTRY_DIAL_CODE,
  isValidMobile10,
  normalizeMobileDigits,
} from '@/utils/agentConfiguration'
import {
  agentTypeLabel,
  isAiAgent,
  normalizeAgentType,
  resolveListAgentType,
} from '@/utils/orgUsers'
import {
  CHATBOT_GENDER_OPTIONS,
  getAgentLanguageOptionsForOrg,
} from '@/utils/agentOtherSettings'
import '@/styles/users/team-form-dialog.css'

function formFromUser(user) {
  return {
    user_name: user?.name && user.name !== '—' ? user.name : '',
    country_code: user?.countryCode || DEFAULT_COUNTRY_DIAL_CODE,
    mobile_number: user?.mobileNumber || '',
    business_hours_start: user?.businessHoursStart ?? user?.business_hours_start ?? '',
    business_hours_end: user?.businessHoursEnd ?? user?.business_hours_end ?? '',
    agent_gender: user?.agentGender ?? user?.agent_gender ?? '',
    agent_language: user?.agentLanguage ?? user?.agent_language ?? '',
    // normalizeAgentType(user?.agentType) alone defaults an UNSET agentType
    // to AI_AGENT (DEFAULT_AGENT_TYPE) -- correct for a user genuinely
    // created as an AI agent, wrong for any real admin/human team member
    // whose agentType was simply never set (the common case for accounts
    // that predate this field). That misclassification hid this whole
    // dialog's phone/role/business-hours fields behind the AI-agent-only
    // "just a display name" view for real people with a real phone number
    // on file. resolveListAgentType (already used to get this right on the
    // Team list/table) falls back to inferring HUMAN from a present
    // email/phone before ever defaulting to AI -- same fix, applied here.
    current_agent_type: resolveListAgentType(user || {}),
  }
}

export default {
  name: 'EditUserDialog',
  mixins: [orgUsersApi],
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    // Same roster UsersPage.vue already has loaded, passed through so a
    // rename can be checked against it client-side -- see CreateUserDialog's
    // identical prop for the create-time version of this check.
    existingUsers: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'saved', 'error'],
  data() {
    return {
      form: formFromUser(null),
      countryOptions: countryCodeList.map((country) => ({
        ...country,
        label: `${country.dial_code} · ${country.name}`,
      })),
      isValid: false,
      saving: false,
      countryCodeRules: [(v) => !!v?.trim() || 'Country code is required'],
      mobileNumberRules: [
        (v) => !!v?.trim() || 'Mobile number is required',
        (v) => isValidMobile10(v) || 'Enter a valid 10-digit number',
      ],
    }
  },
  computed: {
    nameRules() {
      return [
        (v) => !!String(v ?? '').trim() || 'Name is required',
        (v) => {
          const normalized = String(v ?? '').trim().toLowerCase()
          if (!normalized) return true
          const collision = this.existingUsers.some(
            (u) => u.id !== this.user?.id &&
              String(u.name ?? '').trim().toLowerCase() === normalized,
          )
          return !collision || 'Someone in this organisation already uses that name'
        },
      ]
    },
    orgBusinessHoursLabel() {
      const org = this.$store.state.organization || {}
      return `${org.business_hours_start || '10:00'}–${org.business_hours_end || '19:00'}`
    },
    isAiAgentSelected() {
      return isAiAgent(this.form.current_agent_type)
    },
    genderOptions() {
      return CHATBOT_GENDER_OPTIONS
    },
    languageOptions() {
      return getAgentLanguageOptionsForOrg(this.$store.state.organization || {})
    },
    typeLabel() {
      return agentTypeLabel(this.form.current_agent_type)
    },
    typeIcon() {
      return this.isAiAgentSelected ? 'mdi-robot-outline' : 'mdi-account-outline'
    },
    typeBadgeClass() {
      return this.isAiAgentSelected
        ? 'us-team-dialog__type-badge--ai'
        : 'us-team-dialog__type-badge--human'
    },
    subtitle() {
      if (this.isAiAgentSelected) return 'Update this AI agent display name'
      const email = this.user?.email
      return email && email !== '—' ? email : 'Update human agent details'
    },
    canSave() {
      const name = String(this.form.user_name ?? '').trim()
      if (!name) return false
      if (this.isAiAgentSelected) return true
      return (
        Boolean(String(this.form.country_code ?? '').trim()) &&
        isValidMobile10(this.form.mobile_number)
      )
    },
  },
  watch: {
    modelValue(open) {
      if (open && this.user) {
        this.form = formFromUser(this.user)
        this.$nextTick(() => this.$refs.formRef?.resetValidation())
      }
    },
    user(next) {
      if (this.modelValue && next) {
        this.form = formFromUser(next)
      }
    },
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false)
    },
    onMobileNumberInput(value) {
      this.form.mobile_number = normalizeMobileDigits(value)
    },
    async submit() {
      const validation = await this.$refs.formRef?.validate()
      if (validation === false || validation?.valid === false) return
      if (!this.user?.id || !this.canSave) return

      const payload = {
        user_id: this.user.id,
        user_name: this.form.user_name.trim(),
        current_agent_type: this.form.current_agent_type,
        country_code: this.form.country_code,
        mobile_number: normalizeMobileDigits(this.form.mobile_number),
        business_hours_start: this.form.business_hours_start || null,
        business_hours_end: this.form.business_hours_end || null,
        ...(this.isAiAgentSelected ? {
          agent_gender: this.form.agent_gender || null,
          agent_language: this.form.agent_language || null,
        } : {}),
      }

      this.saving = true
      const response = await this.updateOrgUser(payload)
      this.saving = false

      if (isApiSuccess(response)) {
        this.$emit('saved', response)
        this.close()
      } else {
        this.$emit('error', response)
      }
    },
  },
}
</script>
